import { UserWithDisplaySettings } from '@/common/types/databaseTypes';
import { useAuthStore } from '@/stores/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoginCredentials, SignUpCredentials } from '@tickitacka/shared';
import { authService } from './authService';

// 기본 인증 쿼리 - 직접 구현 (컴포넌트 내부에서 사용됨)
export function useCheckEmailExists() {
    return useMutation({
        mutationFn: (email: string) => authService.checkEmailExists(email),
    });
}

export function useSignUp() {
    return useMutation({
        mutationFn: (credentials: SignUpCredentials) =>
            authService.signUp(credentials),
        onSuccess: () => {
            console.log('회원가입 성공');
        },
        onError: error => {
            console.error('회원가입 실패:', error);
            throw error;
        },
    });
}

// main-app 전용 로그인 쿼리 (캐시 업데이트 로직 추가)
export function useLogin() {
    const queryClient = useQueryClient();
    const { updateUser } = useAuthStore();

    return useMutation({
        mutationFn: (credentials: LoginCredentials) =>
            authService.login(credentials),
        onSuccess: user => {
            updateUser(user);
            queryClient.setQueryData(['currentUser'], user);
            console.log('로그인 성공');
        },
        onError: error => {
            console.error('로그인 실패:', error);
            throw error;
        },
    });
}

// main-app 전용 로그아웃 쿼리 (캐시 삭제 로직 추가)
export function useLogout() {
    const queryClient = useQueryClient();
    const { logout } = useAuthStore();

    return useMutation({
        mutationFn: () => authService.logout(),
        onSuccess: () => {
            // 로그아웃 성공 시 사용자 관련 캐시 삭제 및 상태 초기화
            queryClient.removeQueries({ queryKey: ['currentUser'] });
            logout();
            console.log('로그아웃 성공');
        },
        onError: error => {
            console.error('로그아웃 실패:', error);
            throw error;
        },
    });
}

// 유저 id로 사용자 정보 조회 훅
export function useUser(id: string) {
    return useQuery<UserWithDisplaySettings | null, Error>({
        queryKey: ['user', id],
        queryFn: () =>
            authService.getUserById(
                id
            ) as Promise<UserWithDisplaySettings | null>,
        enabled: !!id,
    });
}

// 사용자 정보 업데이트 뮤테이션 훅
export function useUpdateUserData() {
    const queryClient = useQueryClient();
    const { updateUser } = useAuthStore();

    return useMutation<
        UserWithDisplaySettings,
        Error,
        { userId: string; userData: Partial<UserWithDisplaySettings> }
    >({
        mutationFn: ({ userId, userData }) =>
            authService.updateUserData(userId, userData),
        onSuccess: updatedUser => {
            // 현재 사용자 정보 캐시 및 스토어 상태 업데이트
            queryClient.setQueryData(['currentUser'], updatedUser);
            updateUser(updatedUser);
            console.log('사용자 정보 업데이트 성공');
        },
        onError: error => {
            console.error('사용자 정보 업데이트 실패:', error);
            throw error;
        },
    });
}

// 비밀번호 업데이트 뮤테이션 훅
export function useUpdatePassword() {
    return useMutation<void, Error, string>({
        mutationFn: (newPassword: string) =>
            authService.updatePassword(newPassword),
        onSuccess: () => {
            console.log('비밀번호 변경 성공');
        },
        onError: error => {
            console.error('비밀번호 변경 실패:', error);
            throw error;
        },
    });
}

// 계정 삭제 뮤테이션 훅
export function useDeleteAccount() {
    const queryClient = useQueryClient();
    const { logout } = useAuthStore();

    return useMutation<void, Error, string>({
        mutationFn: (userId: string) => authService.deleteAccount(userId),
        onSuccess: () => {
            // 계정 삭제 성공 시 모든 관련 캐시 삭제 및 상태 초기화
            queryClient.removeQueries();
            logout();
        },
        onError: error => {
            console.error('계정 삭제 실패:', error);
            throw error;
        },
    });
}

// 인증메일 재전송 뮤테이션 훅
export function useResendConfirmationEmail() {
    return useMutation<void, Error, string>({
        mutationFn: async email => {
            return await authService.resendConfirmationEmail(email);
        },
        onSuccess: () => {
            console.log('인증 이메일이 성공적으로 재전송되었습니다.');
        },
        onError: (error: Error) => {
            console.error('인증 이메일 재전송 실패:', error);
            throw error;
        },
    });
}
